import { useEffect, useState } from "react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "../components/ui/collapsible";
import UnassignedSubmissions from "../components/UnassignedSubmissions";
import { SectionHeader } from "./Settings";
import { toast } from "sonner";
import { Loader2, RefreshCw, CheckCircle2, XCircle, ExternalLink, ChevronRight } from "lucide-react";

export default function GoogleSheet({ embedded = false }) {
  const { user } = useAuth();
  const canEditConfig = hasPermission(user, "google_sheet:edit_config");
  const canSyncNow = hasPermission(user, "google_sheet:sync_now");
  const [settings, setSettings] = useState(null);
  const [csvUrl, setCsvUrl] = useState("");
  const [autoSync, setAutoSync] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [submissionsRefreshKey, setSubmissionsRefreshKey] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/settings/google-sheet");
    setSettings(data);
    setCsvUrl(data.csv_url || "");
    setAutoSync(!!data.auto_sync);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const { data } = await api.put("/settings/google-sheet", { csv_url: csvUrl, auto_sync: autoSync });
      setSettings(data);
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(errorMessage(e, "Enregistrement impossible. Réessayez."));
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/settings/google-sheet/sync");
      const created = data.prospects_created + data.prospects_attached;
      toast.success(`${data.new_submissions} nouvelle(s) soumission(s), ${created} prospect(s) mis à jour`);
      load();
      setSubmissionsRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(errorMessage(e, "Synchronisation impossible. Réessayez."));
    } finally {
      setSyncing(false);
    }
  };

  if (!settings) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-jamora" /></div>;

  return (
    <div className="space-y-4" data-testid="google-sheet-page">
      {embedded ? (
        <SectionHeader title="Google Sheets" description="Configurez et synchronisez la source Google Sheets des prospects." />
      ) : (
        <div>
          <h1 className="font-display font-bold text-2xl text-slate-900">Synchronisation Google Sheet</h1>
          <p className="text-sm text-slate-500">Connectez la feuille de réponses de votre Google Form au CRM.</p>
        </div>
      )}

      <Card className="p-5 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="csv-url">URL CSV publiée</Label>
          <Input
            id="csv-url"
            data-testid="input-sheet-url"
            value={csvUrl}
            onChange={(e) => setCsvUrl(e.target.value)}
            placeholder="https://docs.google.com/spreadsheets/d/.../pub?output=csv"
          />
          {csvUrl && (
            <a href={csvUrl} target="_blank" rel="noreferrer" className="text-xs text-jamora inline-flex items-center gap-1 hover:underline">
              <ExternalLink size={11} /> Ouvrir le lien
            </a>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-slate-100 pt-4">
          <div>
            <div className="text-sm font-medium">Synchronisation automatique</div>
            <div className="text-xs text-slate-500">Toutes les 5 minutes, importe les nouvelles réponses.</div>
          </div>
          <Switch
            checked={autoSync}
            onCheckedChange={setAutoSync}
            data-testid="switch-auto-sync"
          />
        </div>
        <div className="flex gap-2">
          {canEditConfig && (
            <Button
              onClick={save}
              variant="outline"
              data-testid="btn-save-sheet-settings"
            >
              Enregistrer
            </Button>
          )}
          {canSyncNow && (
            <Button
              onClick={sync}
              disabled={syncing || !csvUrl}
              data-testid="btn-sync-now"
            >
              {syncing ? <Loader2 className="animate-spin mr-2" size={14} /> : <RefreshCw size={14} className="mr-2" />}
              Synchroniser maintenant
            </Button>
          )}
        </div>
      </Card>

      <Collapsible open={helpOpen} onOpenChange={setHelpOpen}>
        <CollapsibleTrigger
          data-testid="toggle-sheet-help"
          className="w-full flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 px-1 py-1"
        >
          <ChevronRight size={14} className={`transition-transform ${helpOpen ? "rotate-90" : ""}`} />
          Comment obtenir l'URL CSV publiée ?
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Card className="p-5 bg-slate-50/50 border-slate-200 mt-2">
            <ol className="text-sm text-slate-600 space-y-1 list-decimal ml-5">
              <li>Ouvrez le Google Sheet contenant les réponses du formulaire.</li>
              <li><strong>Fichier → Partager → Publier sur le Web</strong>.</li>
              <li>Choisissez l'onglet <em>Réponses au formulaire 1</em> · format <strong>Valeurs séparées par des virgules (.csv)</strong>.</li>
              <li>Cliquez sur <strong>Publier</strong> et copiez l'URL affichée (elle finit par <code>output=csv</code>).</li>
              <li>Collez-la ci-dessus puis <em>Synchroniser</em>.</li>
            </ol>
          </Card>
        </CollapsibleContent>
      </Collapsible>

      {(settings.last_sync_at || settings.last_error) && (
        <Card className="p-5">
          <div className="font-display font-semibold text-sm text-slate-900 mb-3">Dernière synchronisation</div>
          {settings.last_error ? (
            <div className="flex items-start gap-2 text-sm text-red-600">
              <XCircle size={16} className="mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-medium">Erreur</div>
                <div className="text-xs">{settings.last_error}</div>
              </div>
            </div>
          ) : settings.last_result ? (
            <div className="flex items-start gap-2 text-sm">
              <CheckCircle2 className="text-jamora mt-0.5 flex-shrink-0" size={16} />
              <div className="space-y-1">
                <div className="text-slate-700">
                  <strong>{settings.last_result.new_submissions}</strong> nouvelle(s) soumission(s) ·{" "}
                  <strong>{settings.last_result.prospects_created}</strong> prospect(s) créé(s) ·{" "}
                  <strong>{settings.last_result.prospects_attached}</strong> rattachée(s) à un prospect existant ·{" "}
                  <strong>{settings.last_result.already_synced}</strong> déjà synchronisée(s)
                  {settings.last_result.ambiguous > 0 && (
                    <> · <strong>{settings.last_result.ambiguous}</strong> à vérifier manuellement (correspondance ambiguë)</>
                  )}
                  {" "}· <strong>{settings.last_result.error_count || 0}</strong> erreur(s)
                </div>
                <div className="text-xs text-slate-500">
                  {new Date(settings.last_sync_at).toLocaleString("fr-FR")}
                </div>
                {settings.last_result.errors?.length > 0 && (
                  <ul className="text-xs text-red-600 mt-2 space-y-0.5">
                    {settings.last_result.errors.map((e, i) => (
                      <li key={i}>Ligne {e.row + 2} : {e.error}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : null}
        </Card>
      )}

      {canSyncNow && <UnassignedSubmissions refreshKey={submissionsRefreshKey} />}
    </div>
  );
}
